// Radar-kontrakt — marin/militær roterende radar inn i SKADE-sjøkartet.
//
// HVA DETTE ER: wire-formatet mellom radarbroen (som taler radarens eget
// multicast-/WS-protokoll) og SKADE-hub → COP. Kontrakten er bevisst holdt
// nærmere radaren enn `Track`: en spoke er en RÅ måling langs én asimut, ikke et
// fusjonert spor. Sporene kommer i tillegg, gjennom `@skade/radar`-trackeren, og
// går ut som vanlige `Track` (se `radarSporTilTrack`).
//
// HVORFOR IKKE BARE `Track`: et radarbilde er 2048 asimuter × 1024 celler med
// nivåverdier. Det er et BILDE, ikke en liste objekter. Å presse det gjennom
// spor-modellen ville kastet bort nettopp signaturen operatøren skal se.
//
// LEGEND ER IKKE VALGFRITT. Bytene i `data` er IKKE kalibrert amplitude — de er
// symbolverdier definert av radarbroen. Uten `RadarLegend` kan de ikke tolkes.
// Derfor er legend en del av kontrakten, og en klient som ikke har mottatt legend
// SKAL ikke tegne noe. Se `vale/RADAR_VURDERING.md` §2.
import { z } from 'zod';
// ── Legend: hvordan en byte i spoke-dataen skal forstås ─────────────────────
/**
 * Hva en legend-verdi BETYR. Skillet mellom `nivå` og doppler-flaggene er det
 * viktigste i hele fila: doppler er en KLASSIFISERING radaren har gjort, ikke et
 * målt spektrum. En verdi kan derfor ikke sammenlignes numerisk med et nivå.
 */
export const LegendType = z.enum([
    'ingen-retur', // under terskel / ikke belyst
    'nivå', // ekkostyrke, monotont stigende innenfor sin egen serie
    'nærmer-seg', // doppler-flagg (HALO m.fl.) — IKKE et nivå
    'fjerner-seg', // doppler-flagg — IKKE et nivå
    'historikk', // radarens egen trail-verdi, om broen sender den
    'ukjent', // broen ga en verdi vi ikke kjenner — tegnes, men markeres
]);
export const Rgba = z.object({
    r: z.number().int().min(0).max(255),
    g: z.number().int().min(0).max(255),
    b: z.number().int().min(0).max(255),
    a: z.number().int().min(0).max(255).default(255),
});
export const LegendOppforing = z.object({
    /** Byteverdien i `RadarSpoke.data` denne oppføringen gjelder for (0–255). */
    verdi: z.number().int().min(0).max(255),
    type: LegendType,
    /** Radarbroens egen foreslåtte farge. Vi kan overstyre med egen palett. */
    farge: Rgba.optional(),
    /**
     * Normalisert styrke 0–1 for `type === 'nivå'`. Utledes av broen fra
     * verdiens plass i nivåserien — IKKE fra dBm. Det finnes ingen kalibrering.
     */
    styrke: z.number().min(0).max(1).optional(),
});
export const RadarLegend = z.object({
    /** Radaren legenden gjelder for. */
    radarId: z.string().min(1),
    oppforinger: z.array(LegendOppforing).min(1),
    /** Sier broen at doppler-flagg finnes i denne legenden? */
    harDoppler: z.boolean().default(false),
});
// ── Spoke: én asimut med celler ─────────────────────────────────────────────
/**
 * Én radial måling. `data` er base64 i JSON-transport (binærstrømmen sender
 * bytene rått). Lengden = antall celler; celle *i* dekker
 * `[i, i+1) * rangeM / data.length` meter fra antennen.
 */
export const RadarSpoke = z.object({
    radarId: z.string().min(1),
    /** Spoke-indeks i omdreiningen, 0 .. spokerPerOmdreining-1. */
    indeks: z.number().int().min(0),
    /**
     * Sann peiling i grader (0–360) hvis radaren kjenner kurs/heading. Mangler den,
     * er `indeks` relativ til radarens forover-akse og må roteres i klienten med
     * `RadarInfo.headingDeg`. Vi gjetter ALDRI heading.
     */
    peilingDeg: z.number().min(0).max(360).optional(),
    /** Full rekkevidde for denne spoken i meter (siste celles ytterkant). */
    rangeM: z.number().positive(),
    tsMs: z.number().int().nonnegative(),
    /** Antennens posisjon ved denne spoken, om broen oppgir den (fartøy beveger seg). */
    lat: z.number().min(-90).max(90).optional(),
    lon: z.number().min(-180).max(180).optional(),
    /** Cellenivåer som base64. Tolkes KUN gjennom legend. */
    dataB64: z.string(),
});
// ── Radarens tilstand og kapasitet ──────────────────────────────────────────
export const RadarStatus = z.enum(['av', 'standby', 'varmer', 'sender', 'feil']);
export const RadarInfo = z.object({
    radarId: z.string().min(1),
    /** Menneskelesbart, f.eks. «Babord HALO24» — settes i drift, ikke i kode. */
    navn: z.string().min(1),
    /** Fabrikat/modell slik broen rapporterer det. Aldri antatt. */
    fabrikat: z.string().optional(),
    status: RadarStatus,
    spokerPerOmdreining: z.number().int().positive(),
    /** Maks celler per spoke. Leses fra broen — ALDRI fra datablad. */
    cellerPerSpoke: z.number().int().positive(),
    /** Omdreininger per minutt, om kjent. */
    rpm: z.number().positive().optional(),
    /** Antennens posisjon. Mangler den, kan bildet ikke georefereres. */
    lat: z.number().min(-90).max(90).optional(),
    lon: z.number().min(-180).max(180).optional(),
    /** Fartøyets/plattformens kurs. Kreves for head-up og for relativ peiling. */
    headingDeg: z.number().min(0).max(360).optional(),
    /** Fart over grunn i knop — brukes til sann-bevegelse-trails og CPA. */
    sogKn: z.number().nonnegative().optional(),
    cogDeg: z.number().min(0).max(360).optional(),
    harDoppler: z.boolean().default(false),
    /** Antall blindsoner (no-transmit) radaren støtter. 0 = ingen støtte. */
    blindsonerStottet: z.number().int().min(0).default(0),
});
// ── Kontroller ──────────────────────────────────────────────────────────────
/**
 * Kanoniske kontrollnavn. Settet er valgt for funksjonsparitet med et modent
 * marint radar-plugin — samme knapper, samme betydning. En kontroll som radaren
 * ikke støtter, skal broen utelate fra `RadarKapabilitet`; UI-et bygges av
 * kapabiliteten, ikke av denne lista. Da får vi aldri en knapp som ikke virker.
 */
export const RadarKontrollNavn = z.enum([
    'status', // av/standby/sender
    'range', // rekkevidde (m)
    'gain',
    'sjoklotter', // sea clutter
    'regnklotter', // rain clutter
    'interferensfilter', // interference rejection
    'malutvidelse', // target expansion
    'malforsterkning', // target boost
    'malseparasjon', // target separation
    'stoyfilter', // noise rejection
    'doppler',
    'dopplerautospor',
    'sidelobedemping', // side lobe suppression
    'lokalinterferens', // local interference filter
    'skannehastighet', // scan speed
    'antennehoyde',
    'antenneforover',
    'antennestyrbord',
    'peilingjustering', // bearing alignment
    'modus', // havn/kyst/vær/fugl
    'sjotilstand', // sea state
    'hovedsmellsdemping', // main bang suppression
    'visningstiming', // display timing
    'rangejustering',
    'ftc', // fast time constant
    'accentlys',
]);
/**
 * Hva én kontroll kan gjøre PÅ DENNE radaren. Broen fyller den ut; UI-et
 * genereres av den. `verdier` brukes for oppregnede kontroller (modus,
 * skannehastighet), `min`/`maks`/`steg` for numeriske.
 */
export const RadarKontrollKapabilitet = z.object({
    navn: RadarKontrollNavn,
    min: z.number().optional(),
    maks: z.number().optional(),
    steg: z.number().positive().optional(),
    /** Oppregnede valg: verdi → etikett. */
    verdier: z.array(z.object({ verdi: z.number(), etikett: z.string() })).optional(),
    /** Kan kontrollen settes i AUTO? */
    auto: z.boolean().default(false),
    enhet: z.string().optional(),
    /** Radaren rapporterer kontrollen, men den kan ikke settes herfra. */
    kunLesing: z.boolean().default(false),
});
export const RadarKontrollVerdi = z.object({
    navn: RadarKontrollNavn,
    verdi: z.number().optional(),
    auto: z.boolean().optional(),
});
export const RadarKapabilitet = z.object({
    radarId: z.string().min(1),
    kontroller: z.array(RadarKontrollKapabilitet),
    /** Rekkeviddetrinnene radaren faktisk tilbyr (m). Tom = fritt innenfor min/maks. */
    rangeTrinnM: z.array(z.number().positive()).default([]),
});
// ── Blindsoner (no-transmit) ────────────────────────────────────────────────
/**
 * Sektor radaren IKKE skal sende i. Reell operativ funksjon: verner personell og
 * naboantenner, og er en av de få tingene som faktisk kan skade noe hvis den
 * settes feil. Derfor egen kontrakt med validering, ikke to løse tall.
 */
export const Blindsone = z.object({
    indeks: z.number().int().min(0),
    aktiv: z.boolean(),
    /** Relativ peiling (til antennens forover-akse), grader. */
    fraDeg: z.number().min(0).max(360),
    tilDeg: z.number().min(0).max(360),
});
// ── Konvolutt over WS ───────────────────────────────────────────────────────
export const RADAR_KANAL = 'radar';
/**
 * Meldingene broen kan sende. `spoke` er den varme veien (tusenvis per sekund) og
 * holdes derfor så liten som mulig; alt annet er sjeldent.
 */
export const RadarMelding = z.discriminatedUnion('type', [
    z.object({ type: z.literal('info'), info: RadarInfo }),
    z.object({ type: z.literal('legend'), legend: RadarLegend }),
    z.object({ type: z.literal('kapabilitet'), kapabilitet: RadarKapabilitet }),
    z.object({ type: z.literal('spoke'), spoke: RadarSpoke }),
    z.object({
        type: z.literal('kontroll'),
        radarId: z.string().min(1),
        verdier: z.array(RadarKontrollVerdi),
    }),
    z.object({
        type: z.literal('blindsoner'),
        radarId: z.string().min(1),
        soner: z.array(Blindsone),
    }),
    z.object({
        type: z.literal('borte'),
        radarId: z.string().min(1),
        /** Hvorfor broen mistet radaren — vises til operatøren, aldri skjult. */
        arsak: z.string(),
    }),
]);
/** Kommandoer klienten kan sende TILBAKE til broen. */
export const RadarKommando = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('settKontroll'),
        radarId: z.string().min(1),
        navn: RadarKontrollNavn,
        verdi: z.number().optional(),
        auto: z.boolean().optional(),
    }),
    z.object({
        type: z.literal('settBlindsone'),
        radarId: z.string().min(1),
        sone: Blindsone,
    }),
    z.object({ type: z.literal('hentTilstand'), radarId: z.string().min(1) }),
]);
// ── Hjelpere ────────────────────────────────────────────────────────────────
// Base64 er implementert RENT her, uten `atob`/`btoa` og uten `Buffer`.
// Grunnen er en reell begrensning i pakken: `@skade/contracts` bygges også til
// nettleseren, så den kan ikke ha node-typer; men den kjører også i hubben, som
// ikke har `atob` i alle Node-versjoner vi støtter. To kodeveier ville dessuten
// betydd at spoke-dekodingen kunne oppføre seg ulikt i tjener og klient — på det
// laveste laget i radarkjeden. Én vei, samme bytes, testbar.
const B64_ALFABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
const B64_OMVENDT = (() => {
    const t = new Int16Array(256).fill(-1);
    for (let i = 0; i < B64_ALFABET.length; i++)
        t[B64_ALFABET.charCodeAt(i)] = i;
    return t;
})();
/**
 * Base64 → bytes. Ukjente tegn (linjeskift, mellomrom) hoppes over; `=` avslutter.
 * Ugyldig inndata gir de bytene som KAN dekodes, ikke et unntak — en enkelt
 * korrupt spoke skal ikke velte hele strømmen. Tapte spokes vises som hull i
 * bildet, som er den riktige oppførselen.
 */
export function b64TilBytes(b64) {
    const ut = new Uint8Array(((b64.length * 3) >> 2) + 3);
    let n = 0;
    let akk = 0;
    let biter = 0;
    for (let i = 0; i < b64.length; i++) {
        const c = b64.charCodeAt(i);
        if (c === 61)
            break; // '='
        const v = c < 256 ? B64_OMVENDT[c] : -1;
        if (v < 0)
            continue;
        akk = (akk << 6) | v;
        biter += 6;
        if (biter >= 8) {
            biter -= 8;
            ut[n++] = (akk >> biter) & 0xff;
        }
    }
    return ut.subarray(0, n);
}
/** bytes → base64, med `=`-utfylling. */
export function bytesTilB64(b) {
    let s = '';
    let i = 0;
    for (; i + 2 < b.length; i += 3) {
        const v = (b[i] << 16) | (b[i + 1] << 8) | b[i + 2];
        s +=
            B64_ALFABET[(v >> 18) & 63] +
                B64_ALFABET[(v >> 12) & 63] +
                B64_ALFABET[(v >> 6) & 63] +
                B64_ALFABET[v & 63];
    }
    const rest = b.length - i;
    if (rest === 1) {
        const v = b[i] << 16;
        s += B64_ALFABET[(v >> 18) & 63] + B64_ALFABET[(v >> 12) & 63] + '==';
    }
    else if (rest === 2) {
        const v = (b[i] << 16) | (b[i + 1] << 8);
        s +=
            B64_ALFABET[(v >> 18) & 63] +
                B64_ALFABET[(v >> 12) & 63] +
                B64_ALFABET[(v >> 6) & 63] +
                '=';
    }
    return s;
}
/**
 * Peiling for en spoke, i grader sann. Rekkefølgen er bevisst:
 *   1. broens `peilingDeg` hvis den finnes (radaren kjenner heading),
 *   2. ellers indeks → relativ peiling, rotert med `headingDeg`,
 *   3. ellers relativ peiling RÅ — og da er bildet head-up, ikke nord-opp.
 * Punkt 3 er grunnen til at UI-et må vise orienteringen eksplisitt: uten heading
 * KAN vi ikke tegne nord-opp, og å late som ville vært en løgn om bildet.
 */
export function spokePeiling(spoke, spokerPerOmdreining, headingDeg) {
    if (spoke.peilingDeg !== undefined)
        return { deg: spoke.peilingDeg % 360, sann: true };
    const rel = (spoke.indeks / spokerPerOmdreining) * 360;
    if (headingDeg === undefined)
        return { deg: ((rel % 360) + 360) % 360, sann: false };
    return { deg: ((rel + headingDeg) % 360 + 360) % 360, sann: true };
}
//# sourceMappingURL=radar.js.map